import { type JSX, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { ThirdPartyForNonTprmUser, ThirdPartyResponse } from 'Models/TPRM';

import { ManageThirdPartyForm } from './Components/ManageThirdPartyForm/ManageThirdPartyForm';

export interface ManageThirdPartyProps {
    authHandler: AuthHandler;
    tprmApi: ClientTPRMApi;
}

export const ManageThirdParty = (props: ManageThirdPartyProps): JSX.Element => {
    const { thirdPartyId } = useParams();
    const thirdPartyIdExists = thirdPartyId !== undefined;

    const userRoles = props.authHandler.getUserRoles();
    const userHasFullTprmAccess = userHasAuthorizedRole(userRoles, TPRM_ROLES);

    if (thirdPartyIdExists) {
        if (userHasFullTprmAccess) {
            return <UserWithFullTprmAccessUpdatingThirdParty thirdPartyId={thirdPartyId} tprmApi={props.tprmApi} />;
        } else {
            return <UserWithLimitedTprmAccessUpdatingThirdParty thirdPartyId={thirdPartyId} tprmApi={props.tprmApi} />;
        }
    } else {
        if (userHasFullTprmAccess) {
            return <UserWithFullTprmAccessCreatingThirdParty tprmApi={props.tprmApi} />;
        } else {
            return <UserWithLimitedTprmAccessCreatingThirdParty tprmApi={props.tprmApi} />;
        }
    }
};

const UserWithLimitedTprmAccessCreatingThirdParty = (props: { tprmApi: ClientTPRMApi }): JSX.Element => {
    return <ManageThirdPartyForm userHasFullTprmAccess={false} thirdPartyExists={false} tprmApi={props.tprmApi} />;
};

const UserWithLimitedTprmAccessUpdatingThirdParty = (props: { thirdPartyId: string; tprmApi: ClientTPRMApi }): JSX.Element => {
    const [thirdParty, setThirdParty] = useState<ThirdPartyForNonTprmUser>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    useEffect(() => {
        const getThirdParty = async (): Promise<void> => {
            try {
                const response = await props.tprmApi.getThirdPartyDetailsForNonTprmUser(props.thirdPartyId);
                setThirdParty(response.data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        getThirdParty();
    }, [props.tprmApi, props.thirdPartyId]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (thirdParty === undefined) {
        return <Placeholder />;
    } else {
        return <ManageThirdPartyForm userHasFullTprmAccess={false} thirdPartyExists={true} thirdParty={thirdParty} tprmApi={props.tprmApi} />;
    }
};

const UserWithFullTprmAccessCreatingThirdParty = (props: { tprmApi: ClientTPRMApi }): JSX.Element => {
    return <ManageThirdPartyForm userHasFullTprmAccess={true} thirdPartyExists={false} tprmApi={props.tprmApi} />;
};

const UserWithFullTprmAccessUpdatingThirdParty = (props: { thirdPartyId: string; tprmApi: ClientTPRMApi }): JSX.Element => {
    const [thirdParty, setThirdParty] = useState<ThirdPartyResponse>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    useEffect(() => {
        const getThirdParty = async (): Promise<void> => {
            try {
                const response = await props.tprmApi.getThirdPartyDetails(props.thirdPartyId);
                setThirdParty(response.data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        getThirdParty();
    }, [props.tprmApi, props.thirdPartyId]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (thirdParty === undefined) {
        return <Placeholder />;
    } else {
        return <ManageThirdPartyForm userHasFullTprmAccess={true} thirdPartyExists={true} thirdParty={thirdParty} tprmApi={props.tprmApi} />;
    }
};
